import { useCallback, useMemo } from "react";
import RadioButton from "../../components/Atomos/Radio/RadioButton";
import TextArea from "../../components/Atomos/Inputs/TextArea";
import Input from "../../components/Atomos/Inputs/Input";
import DropDownList from "../../components/Atomos/DropDown/Dropdown";
import {
  EventTypeEnum,
  SetupTypeEnum,
  SetupType,
  EventType,
} from "../../Enums/EventEnums";
import { useEventForm } from "../../hooks/Events/useEventForm";
import { useEventValidation } from "../../hooks/Events/useEventValidation";

interface GeneralSettingsProps {
  validation?: ReturnType<typeof useEventValidation>;
}

const EventsPage = ({ validation }: GeneralSettingsProps) => {
  const { event, updateEventField } = useEventForm();

  const localValidation = useEventValidation();
  const validationHook = validation || localValidation;

  const getFieldError = useCallback(
    (fieldName: string) => {
      if (!validationHook.hasAttemptedSave) {
        return undefined;
      }
      return validationHook.errors[fieldName];
    },
    [validationHook.hasAttemptedSave, validationHook.errors]
  );

  const hasFieldError = useCallback(
    (fieldName: string) => {
      return (
        validationHook.hasAttemptedSave && !!validationHook.errors[fieldName]
      );
    },
    [validationHook.hasAttemptedSave, validationHook.errors]
  );

  const updateFieldWithValidation = useCallback(
    (fieldName: string, value: any) => {
      updateEventField(fieldName as any, value);
      if (validationHook.hasAttemptedSave) {
        validationHook.validateField(fieldName, value, event);
      }
    },
    [updateEventField, validationHook, event]
  );

  const handleSetupTypeChange = useCallback(
    (opt: { id: string; name: string } | null) => {
      const value = opt?.id ? (opt.id as SetupType) : null;
      updateFieldWithValidation("setupType", value);
    },
    [updateFieldWithValidation]
  );

  const handleEventTypeChange = useCallback(
    (newEventType: EventType) => {
      updateEventField("eventType", newEventType);

      if (validationHook.hasAttemptedSave) {
        const updatedEvent = { ...event, eventType: newEventType };

        validationHook.validateField("eventType", newEventType, updatedEvent);

        validationHook.validateField("location", event.location, updatedEvent);
        validationHook.validateField(
          "countryName",
          event.address?.countryName,
          updatedEvent
        );
        validationHook.validateField(
          "cityName",
          event.address?.cityName,
          updatedEvent
        );
      }
    },
    [updateEventField, validationHook, event]
  );

  type EventTypeGeneral = Exclude<EventType, "special">;

  const availableEventTypes = useMemo(
    () =>
      Object.entries(EventTypeEnum).filter(([key]) => key !== "special") as [
        EventTypeGeneral,
        string,
      ][],
    []
  );

  const setupTypeData = useMemo(
    () =>
      Object.entries(SetupTypeEnum).map(([key, value]) => ({
        id: key,
        name: value,
      })),
    []
  );

  return (
    <div className="flex flex-col gap-6 bg-white p-6 w-full">
      <div className="text-secondary text-xl md:text-2xl font-decimal -ml-1">
        Description
      </div>
      <div className="flex flex-col gap-2 md:gap-[.625rem]">
        <label className="text-lg font-medium block">Event Type</label>
        <div className="flex flex-col md:flex-row gap-x-8 gap-y-2">
          {availableEventTypes.map(([key, value], index) => (
            <RadioButton
              key={key}
              index={index}
              text={value}
              selected={event.eventType === key}
              handleRadioButtonChange={() => handleEventTypeChange(key)}
            />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2 md:col-span-1">
          <DropDownList
            title="Setup Type"
            required
            selectedObjst={
              event.setupType ? SetupTypeEnum[event.setupType as SetupType] : ""
            }
            seter={handleSetupTypeChange}
            error={hasFieldError("setupType")}
            errormsj={getFieldError("setupType")}
            data={setupTypeData}
            getid
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Input
            title="Event Title"
            required
            error={hasFieldError("title")}
            errorMsj={getFieldError("title") || ""}
            name="title"
            value={event.title || ""}
            onChange={(e) => updateFieldWithValidation("title", e.target.value)}
          />
        </div>
      </div>
      <TextArea
        title="Event Description"
        error={hasFieldError("description")}
        errorMsj={getFieldError("description") || ""}
        maxChars={1000}
        characterCount
        name="description"
        value={event.description || ""}
        height="h-5rem"
        onChange={(e) =>
          updateFieldWithValidation("description", e.target.value)
        }
      />
      <TextArea
        title="Overview Tab Description"
        error={hasFieldError("overviewTabDescription")}
        errorMsj={getFieldError("overviewTabDescription") || ""}
        maxChars={3000}
        characterCount
        name="overviewTabDescription"
        value={event.overviewTabDescription || ""}
        height="h-5rem"
        onChange={(e) =>
          updateFieldWithValidation("overviewTabDescription", e.target.value)
        }
      />
    </div>
  );
};

export default EventsPage;
